import React, { useState, useEffect, useRef } from 'react';
import { EmojiEmotions } from '@mui/icons-material';
import { formatRelativeTime } from './utils/dateUtils';

const MessageBubble = ({ message, isOwn, showAvatar, usersWhoReadUpToHere, isLastMessage, onAddReaction, onRemoveReaction, openPickerId, onTogglePicker, user }) => {
  const [hoveredReaction, setHoveredReaction] = useState(null);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  
  const showEmojiPicker = openPickerId === message.id;

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && 
          pickerRef.current && 
          !pickerRef.current.contains(event.target) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target)) {
        onTogglePicker && onTogglePicker(null);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showEmojiPicker, onTogglePicker]);

  const emojis = ['👍', '❤️', '😂', '😮', '😢', '😡'];

  const handleEmojiClick = (emoji) => {
    onAddReaction && onAddReaction(message.id, emoji);
    onTogglePicker && onTogglePicker(null);
  };

  const handlePickerToggle = () => {
    onTogglePicker && onTogglePicker(showEmojiPicker ? null : message.id);
  };

  const handleReactionClick = (emoji, users) => {
    const hasReacted = users.find(u => u.userId === user?.uid);
    if (hasReacted) {
      onRemoveReaction && onRemoveReaction(message.id, emoji);
    } else {
      onAddReaction && onAddReaction(message.id, emoji);
    }
  };

  const reactions = message.reactions || {};
  const hasReactions = Object.entries(reactions).some(([, users]) => users && users.length > 0);
  return (
    <>
      <div className={`message-row ${isOwn ? 'own' : 'other'}${message.isPending ? ' pending' : ''}`}
           data-message-id={message.id}
           data-message-uid={message.uid}>
        
        {!isOwn && (
          <div className="message-avatar-container">
            {showAvatar ? (
              <div className="message-avatar">
                {message.photoURL ? (
                  <img 
                    src={message.photoURL} 
                    alt="Avatar" 
                    className="avatar-img"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="avatar-placeholder-msg">
                    {(message.displayName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ) : (
              <div className="avatar-spacer"></div>
            )}
          </div>
        )}
        
        <div className="message-content">
          <div className="message-bubble-container">
            <div 
              className="message-bubble"
              title={formatRelativeTime(message.timestamp)}
            >
              <span className="message-text">{message.text}</span>
            </div>
            
            {/* Emoji Reaction Button */}
            <div className="emoji-reaction-trigger">
              <button 
                ref={triggerRef}
                className="emoji-add-btn"
                onClick={handlePickerToggle}
                title="Add reaction"
              >
                <EmojiEmotions />
              </button>
              
              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div ref={pickerRef} className={`emoji-picker ${isOwn ? 'picker-own' : 'picker-other'}`}>
                  <div className="emoji-grid">
                    {emojis.map((emoji) => (
                      <button
                        key={emoji}
                        className="emoji-option"
                        onClick={() => handleEmojiClick(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Reactions Display */}
          {hasReactions && (
            <div className="message-reactions">
              {Object.entries(reactions)
                .filter(([, users]) => users && users.length > 0)
                .map(([emoji, users]) => (
                <div 
                  key={emoji}
                  className="reaction-item"
                  onMouseEnter={() => setHoveredReaction({ emoji, users })}
                  onMouseLeave={() => setHoveredReaction(null)}
                  onClick={() => handleReactionClick(emoji, users)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="reaction-emoji">{emoji}</span>
                  <span className="reaction-count">{users.length}</span>
                  
                  {/* Hover Tooltip */}
                  {hoveredReaction?.emoji === emoji && (
                    <div className={`reaction-tooltip ${isOwn ? 'tooltip-own' : 'tooltip-other'}`}>
                      {users.slice(0, 3).map((user) => (
                        <div key={user.userId} className="reaction-user">
                          {user.photoURL ? (
                            <img 
                              src={user.photoURL} 
                              alt={user.displayName}
                              className="reaction-user-avatar"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="reaction-user-avatar-placeholder">
                              {user.displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="reaction-user-name">{user.displayName}</span>
                        </div>
                      ))}
                      {users.length > 3 && (
                        <div className="reaction-more">+{users.length - 3} more</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Read receipts - always right aligned */}
      {isLastMessage && usersWhoReadUpToHere.length > 0 && (
        <div className="message-seen-by">
          <div className="seen-avatars">
            {usersWhoReadUpToHere.slice(0, 3).map((reader) => (
              <div 
                key={reader.userId} 
                className="seen-avatar"
                title={`Seen by ${reader.displayName}`}
              >
                {reader.photoURL ? (
                  <img 
                    src={reader.photoURL} 
                    alt={reader.displayName}
                    className="seen-avatar-img"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="seen-avatar-placeholder">
                    {(reader.displayName || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ))}
          </div>
          <span className="seen-text">
            {usersWhoReadUpToHere.length === 1 
              ? `Seen by ${usersWhoReadUpToHere[0].displayName}`
              : usersWhoReadUpToHere.length === 2
              ? `Seen by ${usersWhoReadUpToHere[0].displayName} and ${usersWhoReadUpToHere[1].displayName}`
              : `Seen by ${usersWhoReadUpToHere[0].displayName} and ${usersWhoReadUpToHere.length - 1} others`
            }
          </span>
        </div>
      )}
    </>
  );
};

export default MessageBubble;